import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';

const gbp = (v) => (v === null || v === undefined ? '—' : `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const qty = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 4 });
const ROLE = { cellar: 'Cellar', location: 'Location' };
const CHECKS = {
  audit_dates_present: 'Audit dates missing', close_date_matches_valuation_date: 'Closing date differs from valuation date',
  closing_fields_present: 'Some products have no closing quantity or value', movement_identity_holds: 'Movements do not balance',
  closing_total_matches_bar_stock_total: 'Closing value differs from the bar stock total', previous_audit_identified: 'Previous audit not found',
  opening_equals_previous_audit_closing: "Opening differs from the previous audit's closing", structure_supported: 'Structure not supported',
  all_ledgers_in_bar_file: 'A ledger in the transactions is missing from the bar file',
  buckets_mapped_to_site_locations: "Some ledgers aren't matched to a site location",
};

const Field = ({ label, children, testId }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className="font-medium text-slate-900" data-testid={testId}>{children}</div>
  </div>
);

export const SourceSummary = ({ s, siteName }) => {
  const issues = [...(s.failed_checks || []).map((k) => CHECKS[k] || k), ...(s.classification_notes || [])];
  return (
    <div className="space-y-3 rounded-lg border bg-white p-4" data-testid="source-summary">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field label="House / site" testId="source-summary-house">{s.house} · {s.name}{siteName ? ` → ${siteName}` : ''}</Field>
        <Field label="Audit period" testId="source-summary-period">{s.open_date || '—'} → {s.close_date || '—'}{s.audit_reference ? ` (audit ${s.audit_reference})` : ''}</Field>
        <Field label="Detected type" testId="source-summary-type">{s.structure?.label}</Field>
        <Field label="Closing stock (source)" testId="source-summary-closing">{qty(s.closing_quantity)} units · {gbp(s.closing_value)}</Field>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="source-summary-locations">
        {s.locations.map((l) => (
          <Badge key={l.code} variant="outline" className={l.role === 'cellar' ? 'border-sky-300 bg-sky-50 text-sky-900' : ''} data-testid={`source-summary-location-${l.code}`}>
            {ROLE[l.role]}: {l.name} · {qty(l.closing_quantity)} · {gbp(l.closing_value)}
          </Badge>
        ))}
        {(s.excluded_ledgers || []).map((l) => (
          <Badge key={l.code} variant="outline" className="border-dashed text-slate-500" data-testid={`source-summary-excluded-${l.code}`}>
            Excluded: {l.name || l.code} ({l.role === 'difference_ledger' ? 'difference ledger' : 'unused'})
          </Badge>
        ))}
      </div>
      {s.source_selection && (
        <p className="text-xs text-slate-500" data-testid="source-summary-selection">
          House {s.source_selection.house} taken from {s.source_selection.original_filename} (houses in file: {s.source_selection.houses_in_archive.join(', ')}; not merged).
        </p>
      )}
      {issues.length > 0 ? (
        <Alert className="border-amber-300 bg-amber-50" data-testid="source-summary-exceptions">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <AlertDescription><ul className="list-disc pl-5 text-sm">{issues.map((x) => <li key={x}>{x}</li>)}</ul></AlertDescription>
        </Alert>
      ) : <p className="text-xs text-emerald-700" data-testid="source-summary-no-exceptions">No source exceptions.</p>}
    </div>
  );
};
